import React from "react";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  BRAND_COLOR,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import BrandButton from "../../common/BrandButton/BrandButton";

export type SalesOrderListToolbarProps = {
  title?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  onNewOrder: () => void;
  isNewOrderLoading?: boolean;
  children?: React.ReactNode;
  isMobile?: boolean;
};

const SalesOrderListToolbar: React.FC<SalesOrderListToolbarProps> = ({
  title,
  searchValue = "",
  onSearchChange,
  searchPlaceholder,
  onNewOrder,
  isNewOrderLoading = false,
  children,
  isMobile = false
}) => {
  const { t } = useKivunovaTranslation();

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper"
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexDirection={isMobile ? "column" : "row"}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 1.5,
              backgroundColor: `${BRAND_COLOR}14`
            }}
          >
            <ShoppingCartOutlinedIcon
              sx={{ color: BRAND_COLOR, fontSize: 22 }}
            />
          </Box>
          <Typography variant="h5" fontWeight="700" color="text.primary">
            {title ?? t("sales-orders", "Sales Orders")}
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          flexDirection={isMobile ? "column" : "row"}
          flexWrap="wrap"
        >
          <BrandButton
            variant="primary"
            onClick={onNewOrder}
            disabled={isNewOrderLoading}
            data-testid="sales-order-toolbar-create-btn"
          >
            {t("new-order", "New Order")}
          </BrandButton>
          {children}
        </Box>
      </Box>

      {onSearchChange && (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={
              searchPlaceholder ??
              t(
                "search-sales-orders-placeholder",
                "Search by order number, customer, or status"
              )
            }
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 1.5,
                backgroundColor: "action.hover",
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: "divider" },
                "&.Mui-focused fieldset": {
                  borderColor: BRAND_COLOR,
                  borderWidth: 1
                }
              }
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default SalesOrderListToolbar;
